/**
 * System announcement banner that appears when there are active announcements.
 * Displays the highest-priority announcement (critical > warning > info).
 * Supports dismissal and "Learn more" links.
 */
import { useState, useMemo, useCallback } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  InformationCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { cn } from '@pantry/ui';
import { useActiveAnnouncements, useDismissAnnouncement } from '@pantry/api-client';
import { selectHighestPriorityAnnouncement } from '../../utils/announcements';
import type { AnnouncementSeverity } from '@pantry/shared';

/** Icon for each severity level */
const SEVERITY_ICONS: Record<
  AnnouncementSeverity,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  info: InformationCircleIcon,
  warning: ExclamationTriangleIcon,
  critical: ExclamationCircleIcon,
};

/** Tailwind classes for each severity level */
const SEVERITY_STYLES: Record<
  AnnouncementSeverity,
  { container: string; text: string; icon: string; button: string }
> = {
  info: {
    container: 'bg-blue-50 border-blue-200',
    text: 'text-blue-800',
    icon: 'text-blue-500',
    button: 'text-blue-600 hover:text-blue-800 hover:bg-blue-100',
  },
  warning: {
    container: 'bg-yellow-50 border-yellow-200',
    text: 'text-yellow-800',
    icon: 'text-yellow-500',
    button: 'text-yellow-600 hover:text-yellow-800 hover:bg-yellow-100',
  },
  critical: {
    container: 'bg-red-50 border-red-200',
    text: 'text-red-800',
    icon: 'text-red-500',
    button: 'text-red-600 hover:text-red-800 hover:bg-red-100',
  },
};

export function AnnouncementBanner() {
  const { t } = useLingui();
  // Fetch active announcements for web platform
  const { data: announcements } = useActiveAnnouncements('web');
  const dismissMutation = useDismissAnnouncement();

  // Track locally dismissed announcements (for immediate UI feedback)
  const [locallyDismissed, setLocallyDismissed] = useState<Set<string>>(new Set());

  // Select the highest priority announcement that hasn't been dismissed
  const announcement = useMemo(
    () => selectHighestPriorityAnnouncement(announcements, locallyDismissed),
    [announcements, locallyDismissed]
  );

  const handleDismiss = useCallback(
    (id: string) => {
      // Immediately update local state for responsive UI
      setLocallyDismissed((prev) => new Set([...prev, id]));

      // Persist dismissal to server
      dismissMutation.mutate(id);
    },
    [dismissMutation]
  );

  const handleLearnMore = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  // Don't render when there's no announcement
  if (!announcement) {
    return null;
  }

  const styles = SEVERITY_STYLES[announcement.severity];
  const Icon = SEVERITY_ICONS[announcement.severity];

  return (
    <div
      className={cn('border-b px-4 py-3 transition-all duration-300 ease-in-out', styles.container)}
      role="alert"
      aria-live="polite"
    >
      <div className="mx-auto flex max-w-6xl items-start gap-3">
        {/* Icon */}
        <Icon className={cn('mt-0.5 h-5 w-5 flex-shrink-0', styles.icon)} aria-hidden="true" />

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <p className={cn('text-sm font-medium', styles.text)}>{announcement.title}</p>

          {/* Message */}
          {announcement.message && (
            <p className={cn('mt-1 text-sm opacity-90', styles.text)}>{announcement.message}</p>
          )}

          {/* Learn More Link */}
          {(() => {
            const url = announcement.linkUrl;
            if (!url) return null;
            return (
              <button
                type="button"
                onClick={() => handleLearnMore(url)}
                className={cn(
                  'mt-2 inline-flex items-center gap-1.5 text-sm font-medium underline-offset-2 hover:underline',
                  styles.text
                )}
              >
                {announcement.linkText || t`Learn more`}
                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            );
          })()}
        </div>

        {/* Dismiss Button */}
        {announcement.dismissible && (
          <button
            type="button"
            onClick={() => handleDismiss(announcement.id)}
            className={cn(
              '-mr-1 -mt-1 rounded-md p-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2',
              styles.button
            )}
            aria-label={t`Dismiss announcement`}
          >
            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}
